<template>
  <div class="registration">
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-2">
          <RegistrationInformationsComponent
            :registration="registration"
            :group="registration?.registrationGroup?.group"
            @downloadTrainingSheet="downloadTrainingSheet"
          />
        </div>
        <div class="col-lg-8">
          <div class="loader-container">
            <RegistrationTabsComponent :tabs="tabs" :key="current" />
            <template v-for="tab in tabs" :key="tab.code">
              <template v-if="tab.id === current">
                <template v-for="block in tab.blocks" :key="block.code">
                  <div class="widget">
                    <div class="widget-heading">
                      <h2 class="textStyle">
                        <FontAwesomeIcon v-if="block.icon" :icon="'fa-light fa-' + block.icon" class="me-1" />
                        {{ block.label }}
                      </h2>
                    </div>
                    <div class="widget-content">
                      <div v-if="block.help" class="alert alert-light-warning" v-html="block.help"></div>
                      <RegistrationQuoteComponent
                        v-if="block.code === 'quote-quote'"
                        :contract="contract"
                        :finacial="finacial"
                        :amount="amount"
                        :trainingData="trainingData"
                        :financialLoading="quoteFinancialLoading"
                        :trainingLoading="quoteTrainingLoading"
                        :contractLoading="quoteContractLoading"
                        :amountLoading="quoteAmountLoading"
                        :isEditable="tab.editable"
                        :isSpecificCalendarFormButtonActive="
                          registration && registration.trainingCourse?.id && registration?.registrationGroup?.id
                        "
                        @getSpecificCalendarForm="getSpecificCalendarForm"
                        @editCost="getEditCostForm"
                      />
                      <RegistrationDocumentsListComponent
                        v-else-if="block.code === 'document-document'"
                        :cerfaModels="cerfaModels"
                        :conventionModels="conventionModels"
                        @updateDocument="updateDocumentFile"
                        @download="downloadDocument"
                      />
                      <RegistrationDocumentsComponent v-else-if="block.code === 'quote-documents'" />
                      <FormComponent
                        v-else-if="block.models"
                        :models="block.models"
                        :hideSubmit="true"
                        :keepHideValue="true"
                        :key="key"
                        @selected="selected"
                        @uploading="uploading"
                        @uploaded="uploaded"
                        @draft="draft"
                        @toggleTab="toggleTab"
                        @selectTypeAction="selectTypeAction"
                        @checkChildrenToReset="checkChildrenToReset"
                        @fetchConditionalChildren="fetchConditionalChildren"
                      />
                    </div>
                  </div>
                </template>
              </template>
            </template>
            <RegistrationControlsComponent
              :tabs="tabs"
              :current="current"
              :disabled="fileLoading"
              :buttons="tabs[current]?.buttons"
              @save="save"
              @next="next"
              @prev="prev"
              @clickButton="clickButton"
            />
          </div>
          <BlockLoader v-if="currentTabCode !== 'tab-quote'" :loading="loading" position="fixed" />
        </div>
        <div class="col-lg-2">
          <RegistrationHelpComponent />
          <RegistrationStepsComponent :steps="steps" />
        </div>
      </div>
    </div>
  </div>
  <RegistrationLoginComponent @loginSuccess="loginSuccess" />
  <ModalFormComponent
    :models="models"
    :title="modalTitle"
    :alert="alert"
    :alertIcon="alertIcon"
    :alertType="alertType"
    :alertTitle="alertTitle"
    :disabledSubmit="disabledModalSubmit"
    @cancel="resetModalContent"
    @hidden="resetModalContent"
    @input="updateCost"
    @submit="submitModal"
  >
    <template v-if="modalType === 'activeAccount'" #formView>
      <div class="row">
        <FormFieldComponent v-for="model in models" :model="model" :key="model.vars.id" />
        <PasswordInfoComponent />
      </div>
    </template>
  </ModalFormComponent>
</template>

<script>
import { mapActions, mapState } from "pinia";
import FormComponent from "@/components/FormComponent.vue";
import RegistrationTabsComponent from "@/components/registration/RegistrationTabsComponent.vue";
import RegistrationQuoteComponent from "@/components/registration/RegistrationQuoteComponent.vue";
import RegistrationControlsComponent from "@/components/registration/RegistrationControlsComponent.vue";
import RegistrationStepsComponent from "@/components/registration/RegistrationStepsComponent.vue";
import RegistrationHelpComponent from "@/components/registration/RegistrationHelpComponent.vue";
import RegistrationInformationsComponent from "@/components/registration/RegistrationInformationsComponent.vue";
import RegistrationLoginComponent from "@/components/registration/RegistrationLoginComponent.vue";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { registrationManager } from "@/services/registration/registration-manager";
import { formValidator } from "@/services/form/form-validator";
import { formManager } from "@/services/form/form-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { colorManager } from "@/services/platform/color-manager";
import { usePlatformStore } from "@/store/platform/platform";
import { useStudeaManagerStore } from "@/store/studea_manager/studea-manager";
import { useRegistrationStore } from "@/store/registration/registration";
import { useMainStore } from "@/store/main/main";
import { useUserStore } from "@/store/user/user";
import { i18n } from "@/i18n";
import { registrationConstante } from "@/constantes/registration";
import { apiConnection } from "@/services/api-connection";
import ModalFormComponent from "@/components/form/ModalFormComponent.vue";
import RegistrationDocumentsListComponent from "@/components/registration/document/RegistrationDocumentsListComponent.vue";
import { messengerMessageManager } from "@/services/messenger-message/messenger-message-manager";
import { userManager } from "@/services/security/user-manager";
import { loginManager } from "@/services/security/login-manager";
import { reactive } from "vue";
import FormFieldComponent from "@/components/form/FormFieldComponent.vue";
import PasswordInfoComponent from "@/components/form/PasswordInfoComponent.vue";

export default {
  name: "PreRegistrationSheetView",
  components: {
    PasswordInfoComponent,
    FormFieldComponent,
    RegistrationDocumentsListComponent,
    ModalFormComponent,
    RegistrationLoginComponent,
    RegistrationInformationsComponent,
    BlockLoader,
    FontAwesomeIcon,
    RegistrationHelpComponent,
    RegistrationStepsComponent,
    RegistrationControlsComponent,
    RegistrationQuoteComponent,
    RegistrationTabsComponent,
    FormComponent,
  },
  data() {
    return {
      tabs: {},
      steps: [],
      registrationCryptedId: this.$route.params.registration,
      groupCryptedId: this.$route.params.group,
      registration: {},
      current: null,
      accountActivated: false,
      alerts: [],
      valid: true,
      loading: false,
      key: 0,
      fileLoading: false,
      quoteTrainingLoading: false,
      quoteContractLoading: false,
      quoteFinancialLoading: false,
      quoteAmountLoading: false,
      postUrl: "",
      dataUrlParams: [],
      modalTitle: "",
      modalAlert: "",
      autoSelectValue: false,
      models: null,
      modalData: "",
      isForm: false,
      currentField: null,
      contract: {},
      finacial: {},
      careDuration: [],
      quoteErrors: {},
      amount: {},
      alert: "",
      alertIcon: "",
      alertTitle: "",
      alertType: "",
      currentYear: null,
      modalType: "",
      updateCostDebounceTimer: null,
      disabledModalSubmit: false,
    };
  },
  computed: {
    ...mapState(useUserStore, {
      user: (store) => store.user,
    }),
    ...mapState(usePlatformStore, {
      design: (store) => store.design,
    }),
    ...mapState(useRegistrationStore, {
      alertCorrespondingTable: (store) => store.alertCorrespondingTable,
    }),

    trainingData() {
      if (this.registration?.registrationGroup?.group) {
        const group = this.registration.registrationGroup;
        return {
          modality: {
            title: this.$tc("registration.modality", 1),
            data: group?.group?.distanceLearning
              ? this.$t("registration.distanciel")
              : this.$t("registration.in_person"),
            badge: "primary",
          },
          startDate: {
            title: this.$t("registration.startDate"),
            data: group.startDate,
          },
          endDate: {
            title: this.$t("registration.endDate"),
            data: group.endDate,
          },
          hourlyTrainingVolume: {
            title: this.$t("registration.hourlyVolume"),
            data: group?.hourlyTrainingVolume,
            baselineTitle: group?.hourlyRemoteTeachingVolume
              ? this.$tc("registration.hourlyRemoteVolume", { volume: group?.hourlyRemoteTeachingVolume })
              : "",
          },
        };
      }
      return {};
    },

    cerfaModels() {
      return {
        vars: {
          label: i18n.global.t("upload_signed_document.label"),
          name: "cerfa",
          attr: {
            "data-validation-rules": '{"minFiles":true}',
            "data-validation-messages": { minFiles: i18n.global.t("upload_signed_document.one_file") },
          },
          id: "cerfa_file",
          required: true,
          value: this.registration.signedCerfa,
          data: this.registration.signedCerfa,
          block_prefixes: ["form", "file_upload", "_cerfa_file"],
          max_files: 1,
          min_files: 0,
          max_file_size: 0,
          validation_rules: '{"minFiles":true}',
          validation_messages: { minFiles: i18n.global.t("upload_signed_document.one_file") },
          extensions_accepted: "(.|/)(pdf)$",
          extensions_accepted_message: i18n.global.t("upload_signed_document.file_only"),
          upload_path: "",
          row_attr: { class: " col-md-6" },
          conf_name: "registration",
          conf_path: "registration",
          conf_subject: this.registrationCryptedId,
        },
      };
    },

    conventionModels() {
      return {
        vars: {
          label: i18n.global.t("upload_signed_document.label"),
          name: "convention",
          attr: {
            "data-validation-rules": '{"minFiles":true}',
            "data-validation-messages": { minFiles: i18n.global.t("upload_signed_document.one_file") },
          },
          id: "convention_file",
          required: true,
          value: this.registration.signedConvention,
          data: this.registration.signedConvention,
          block_prefixes: ["form", "file_upload", "_convention_file"],
          max_files: 1,
          min_files: 0,
          max_file_size: 0,
          validation_rules: '{"minFiles":true}',
          validation_messages: { minFiles: i18n.global.t("upload_signed_document.one_file") },
          extensions_accepted: "(\\.|\\/)(pdf)$",
          extensions_accepted_message: i18n.global.t("upload_signed_document.file_only"),
          upload_path: "",
          row_attr: { class: " col-md-6" },
          conf_name: "registration",
          conf_path: "registration",
          conf_subject: this.registrationCryptedId,
        },
      };
    },

    currentTabCode() {
      const currentTab = Object.values(this.tabs).find((tab) => tab.id === this.current);
      if (!currentTab) return null;
      return currentTab.code;
    },
  },
  watch: {
    $route() {
      this.current = parseInt(this.$route.params.tab);
    },
    user() {
      if (!Object.keys(this.tabs).length) {
        this.getRegistration();
      }
    },
    tabs() {
      this.setAlertCorrespondingTable();
    },
    currentTabCode() {
      this.getQuoteData();
      this.getSalaryData();
    },
  },
  mounted() {
    this.loading = true;
    this.init();
    this.getQuoteData();
    this.getSalaryData(false);
    this.setAppLoading(false);
  },
  beforeUnmount() {
    if (this.updateCostDebounceTimer) {
      clearTimeout(this.updateCostDebounceTimer);
    }
    this.setBreadcrumbPageTitle({});
  },
  methods: {
    async init(fill = null) {
      const magicLink = apiConnection.getMagicLinkToken();
      if (magicLink && !this.accountActivated) {
        const response = await userManager.activateAccount();
        if (response && !response.hasError && !response.alreadyActive) {
          this.models = response;
          this.modalType = "activeAccount";
          this.modalTitle = "Première connexion";
          return;
        }
      }
      this.getRegistration(fill);
    },

    async draft(model) {
      const result = formManager.processField(model, {}, [model]);
      const registrationField = model?.vars?.attr?.["registration-field"];
      if (registrationField && this.groupCryptedId && this.registrationCryptedId) {
        const res = await registrationManager.putOneField(
          this.groupCryptedId,
          registrationField,
          result,
          this.registrationCryptedId,
        );
        this.removeAlerts(formValidator.clearModelAlerts(model));
        if (res.responses[0].validationErrors && res.responses[0].validationErrors.length) {
          this.pushAlerts(formValidator.processResponses(res.responses[0].validationErrors, [model], true), model);
        }
        this.triggerPutOneFieldOnBoundFields(model);
      } else {
        await this.putSalaryField(model);
      }
    },

    async putSalaryField(model) {
      const isFieldsToUpdate = [
        "registration_salary_block_idccHasSMIC",
        "registration_salary_block_previousEarningYear",
      ].includes(model?.vars?.id);

      if (!isFieldsToUpdate) return;

      this.loading = true;
      const result = formManager.processField(model, {}, [model]);
      const response = await registrationManager.putSalaryTab(
        this.$route.params.registration,
        this.$route.params.group,
        this.current,
        result,
      );

      if (response) {
        this.updateTab(response, registrationConstante.REGISTRATION_SALARY_TAB_CODE);
        this.loading = false;
      }
    },

    uploading() {
      this.fileLoading = true;
    },

    uploaded() {
      this.fileLoading = false;
    },

    setBreadcrumbTitle() {
      const apprenticeName = this.registration?.apprentice?.firstName + " " + this.registration?.apprentice?.lastName;
      const companyName = this.registration?.hostCompany?.companyName;
      const pageTitle =
        i18n.global.t("page.pre_registration") + " | " + apprenticeName + (companyName ? " - " + companyName : "");
      this.setBreadcrumbPageTitle({
        name: "TrainingPreRegistration",
        title: pageTitle,
      });
    },

    getRegistration(fill = null) {
      this.setTrainingName("");
      if (!this.registrationCryptedId) {
        return;
      }

      this.resetAlerts();

      registrationManager.form(this.groupCryptedId, this.registrationCryptedId).then((res) => {
        if (res && !res.hasError) {
          this.tabs = reactive(res.tabs);
          this.steps = res.steps;
          this.registration = res.registration;
          this.setTrainingName(this.registration.trainingCourse.training.displayName);

          this.setBreadcrumbTitle();
          colorManager.setPlatformStyle(res.registration.trainingCourse, this.design);

          if (!this.getCurrentTab() && Object.keys(this.tabs).length) {
            const activeTab = Object.values(this.tabs).find((tab) => tab.active === true);
            this.current = activeTab ? activeTab.id : Object.values(this.tabs)[0].id;
          }

          this.fillUser(fill);

          this.$router.push({
            name: "TrainingPreRegistration",
            params: {
              tab: this.current,
              group: this.groupCryptedId,
              registration: this.registrationCryptedId,
            },
            query: this.$route.query,
          });
          this.setAlertCorrespondingTable();
        }
        this.loading = false;
      });
    },

    fillUser(fill) {
      if (Object.keys(this.tabs).length && this.tabs[1] && this.tabs[1].id === parseInt(this.$route.params.tab)) {
        const tab = this.tabs[1];
        let block;
        let fieldsToFill = null;
        if (Object.values(this.user).length) {
          fieldsToFill = {
            firstName: this.user?.firstName,
            lastName: this.user?.lastName,
            email: this.user?.email,
          };
        }

        switch (fill) {
          case registrationConstante.REGISTRATION_ACTOR_APPRENTICE:
            block = tab.blocks.find((block) => block.code === "apprentice-personal-informations");
            break;
          case registrationConstante.REGISTRATION_ACTOR_COMPANY:
            block = tab.blocks.find((block) => block.code === "apprentice-host-company");
            fieldsToFill = {
              alreadyFoundCompany: true,
            };
            break;
        }

        if (block) {
          Object.entries(fieldsToFill).forEach(([field, objectValue]) => {
            const target = block.models.find((item) => item.vars.name === field);

            if (target && objectValue) {
              target.vars.value = objectValue;
              formValidator.validModel(target);
            }
          });
          this.key++;
        }
      }
    },

    async save(save = true, formValidation = false, validateStep = false, prev = false, type = null) {
      const ret = this.processStep(formValidation);
      if (this.groupCryptedId && this.registrationCryptedId && !ret.hasError) {
        this.loading = true;
        delete ret.hasError;
        delete ret.errors;
        const response = await registrationManager.save(
          ret,
          this.groupCryptedId,
          this.$route.params.tab,
          this.registrationCryptedId,
          validateStep,
          type,
        );

        if (!response.hasError) {
          const registration = response?.responses[0]?.object?.registration;
          notificationManager.showNotification("success", i18n.global.t("registration.save.success"));

          let nextTabId = prev ? registration.prevTabId : registration.nextTabId;

          if (save) {
            nextTabId = this.$route.params.tab;
          }

          this.updateCurrentTab(registration?.currentTab);
          this.goToTab(nextTabId, registration.cryptedId);
          this.updateSteps(registration?.steps);
          this.validCurrentRegistration(
            registration?.hasCurrentRegistrationTabBeenValidated,
            registration?.currentRegistrationTabId,
          );
          this.updateNextTab(registration?.nextTab, registration?.nextTabId);
        } else if (response.responses && response.responses[0].validationErrors) {
          notificationManager.showNotification("error", this.$t("signed_error"));
          const tab = this.getCurrentTab();
          if (tab) {
            Object.values(tab.blocks).forEach((block) => {
              formValidator.processResponses(response.responses[0].validationErrors, block.models);
            });
          }
        }
        this.loading = false;
      }
    },

    updateSteps(steps) {
      if (steps && Object.keys(steps).length) {
        this.steps = steps;
      }
    },

    updateCurrentTab(currentTab) {
      if (currentTab) {
        this.tabs[this.current] = currentTab;
      }
    },

    updateNextTab(nextTab, nextTabId) {
      if (nextTabId && nextTab) {
        this.tabs[nextTabId] = nextTab;
      }
    },

    validCurrentRegistration(hasCurrentRegistrationTabBeenValidated, currentRegistrationTabId) {
      if (hasCurrentRegistrationTabBeenValidated && currentRegistrationTabId) {
        this.tabs[currentRegistrationTabId].valid = true;
      }
    },

    next() {
      const type = this.currentTabCode === registrationConstante.REGISTRATION_SALARY_TAB_CODE ? "salary" : "";
      this.save(false, true, true, false, type);
    },

    prev() {
      const type = this.currentTabCode === registrationConstante.REGISTRATION_SALARY_TAB_CODE ? "salary" : "";
      this.save(false, false, false, true, type);
    },

    toggleTab(tabId) {
      this.goToTab(tabId, this.registrationCryptedId);
    },

    goToTab(tab, registration = null) {
      const params = { tab: tab, group: this.groupCryptedId, registration: this.registrationCryptedId };
      if (registration) {
        params["registration"] = registration;
      }
      this.$router.push({
        name: "TrainingPreRegistration",
        params: params,
        query: this.$route.query,
      });
    },

    getCurrentTab() {
      return Object.values(this.tabs).find((tab) => tab.id === this.current);
    },

    processStep(formValidation = true) {
      let ret = [];
      const tab = this.getCurrentTab();

      if (tab && tab.blocks && Object.keys(tab.blocks).length) {
        Object.values(tab.blocks).forEach((block) => {
          if (block && block.models && Object.keys(block.models).length) {
            const models = block.models;
            let errors = [];
            if (formValidation) {
              errors = formValidator.validForm(models);
              this.valid = !errors.length;
              ret = { hasError: !this.valid, errors: errors };
            }
            if (this.valid) {
              ret = { ...ret, ...formManager.processForm(models) };
            }
          }
        });
      }
      return ret;
    },

    selected(model) {
      formValidator.validModel(model);
      this.draft(model);
      const fieldName = model.vars.name;
      const config = this.getFieldConfig(fieldName);
      const values = model.vars.value?.object;

      if (!config || !values) return;

      const tab = Object.values(this.tabs).find((tab) => tab.code === config.tabCode);
      if (!tab) return;

      const fieldPrefix = fieldName.split("::")[0];
      config.blockCodes.forEach((blockCode) => {
        const block = tab.blocks.find((block) => block.code === blockCode);
        if (!block?.models) return;

        this.populateBlockFields(block.models, fieldPrefix, values);
      });
    },

    getFieldConfig(fieldName) {
      const fieldConfigs = {
        "hostCompany::inseeCompany": {
          tabCode: "tab-company",
          blockCodes: ["company-informations", "company-opco"],
        },
        "administratorCompany::inseeCompany": {
          tabCode: "tab-company",
          blockCodes: ["company-head-office"],
        },
      };
      return fieldConfigs[fieldName];
    },

    populateBlockFields(models, fieldPrefix, values) {
      const fieldMapping = {
        siretNumber: "siret",
        companyName: "socialReason",
        nafCode: "nafNomenclatureCompanyActivity",
        totalWorkforce: "establishmentWorkforceRangeMin",
        address1: "address",
        addressComplement: "addressComplement",
        addressCity: "addressCity",
        opco: "opco",
        idcc: "branch",
      };

      Object.entries(fieldMapping).forEach(([field, objectName]) => {
        const target = models.find((item) => item.vars.name === `${fieldPrefix}::${field}`);
        if (!target) return;

        const objectValue = this.transformFieldValue(field, values[objectName], target);

        if (objectValue) {
          target.vars.value = objectValue;
          formValidator.validModel(target);
          const registrationField = target?.vars?.attr?.["registration-field"];

          if (registrationField && this.groupCryptedId && this.registrationCryptedId) {
            const result = formManager.processField(target, {}, [target]);

            registrationManager
              .putOneField(this.groupCryptedId, registrationField, result, this.registrationCryptedId)
              .then((res) => {
                const cleared = formValidator.clearModelAlerts(target);
                this.removeAlerts(cleared);
                this.toggleAlerts(res, target);
              });
          }
        }
      });
    },

    transformFieldValue(field, value, target) {
      if (!value) return null;

      const transformers = {
        addressCity: (val) => ({
          value: val.id,
          data: val.id,
          label: `${val.name} (${val.postCode})`,
        }),
        idcc: (val) => ({
          value: val.id,
          data: val.id,
          label: `${val.code} - ${val.title}`,
        }),
        opco: (val) => target.vars.choices.find((choice) => choice.data.id === val.id),
        nafCode: (val) => val?.code,
      };

      return transformers[field] ? transformers[field](value) : value;
    },

    loginSuccess(fill) {
      this.loading = true;
      this.init(fill);
    },

    setAlertCorrespondingTable() {
      Object.values(this.tabs).forEach((tab) => {
        tab.blocks.forEach((block) => {
          Object.values(block.models).forEach((model) => {
            if (model?.vars?.attr?.alerts?.length) {
              model.vars.attr.alerts.forEach((alert) => {
                if (alert.boundFields && alert.boundFields.length) {
                  alert.boundFields.forEach((element) => {
                    const item = {
                      name: element,
                      field: model.vars.name,
                    };
                    this.addAlertToCorrespondingTable(item, tab.id, block.code);
                  });
                }
                if (alert.isTriggered) {
                  alert["fieldName"] = model.vars.label;
                  this.pushAlert(alert);
                  if (alert.level > 1) {
                    model.vars.isInvalid = true;
                  }
                }
              });
            }
          });
        });
      });
    },

    triggerPutOneFieldOnBoundFields(model) {
      const boundElements = this.alertCorrespondingTable[model.vars.name];
      if (!boundElements?.length) return;

      boundElements.forEach((element) => {
        const currentTab = this.tabs[element.tab];
        const currentBlock = currentTab?.blocks.find((block) => block.code === element.block);

        if (!currentBlock) return;

        element.fields.forEach((fieldName) => {
          const currentModel = currentBlock.models.find((model) => model.vars.name === fieldName);
          const registrationField = currentModel?.vars?.attr?.["registration-field"];

          if (registrationField && this.groupCryptedId && this.registrationCryptedId) {
            const result = formManager.processField(currentModel, {}, [currentModel]);

            registrationManager
              .putOneField(this.groupCryptedId, registrationField, result, this.registrationCryptedId)
              .then((res) => {
                const cleared = formValidator.clearModelAlerts(currentModel);
                this.removeAlerts(cleared);
                this.toggleAlerts(res, currentModel);
              });
          }
        });
      });
    },

    toggleAlerts(res, currentModel) {
      const alerts = res?.responses[0]?.validationErrors;
      formValidator.clearModelAlerts(currentModel);

      if (alerts && alerts.length) {
        alerts.forEach((alert) => {
          const currentAlert = currentModel.vars.attr.alerts.find(
            (item) => Number(item.id) === Number(alert.context.alertId),
          );
          if (currentAlert) {
            currentAlert.isTriggered = true;
            currentAlert["fieldName"] = currentModel.vars.label;
            this.pushAlert(currentAlert);
          }
        });
      }
    },

    resetModalContent() {
      this.modalTitle = "";
      this.models = null;
      this.postUrl = "";
      this.modalAlert = "";
      this.isForm = false;
      this.alertTitle = "";
      this.alert = "";
      this.alertIcon = "";
      this.alertType = "";
      this.currentYear = null;
      this.disabledModalSubmit = false;
    },

    selectTypeAction(payload) {
      const button = payload.button;
      this.resetModalContent();
      this.setModalConfiguration(button);

      if (button.dataUrl) {
        this.getModalFormModels(button, payload.model);
      }
    },

    setModalConfiguration(button) {
      this.modalTitle = button.title ?? "";
      this.isForm = button.isForm;
      this.modalAlert = button.alert ?? "";
      this.autoSelectValue = button.autoSelectValue ?? false;
    },

    getModalFormModels(button, model) {
      this.loading = true;
      const url = this.addParamsFromFrontUrl(button.dataUrlParams, button.dataUrl);
      apiConnection.get(url).then((response) => {
        this.postUrl = button.postUrl ?? "";
        this.dataUrlParams = button.dataUrlParams ?? [];
        this.currentField = model;
        this.modalType = "contact";
        this.setModalContent(response);
        this.loading = false;
      });
    },

    addParamsFromFrontUrl(params, buttonUrl) {
      let url = buttonUrl;

      if (params && params.length) {
        params.forEach((param) => {
          url += "/" + this.$route.params[param];
        });
      }
      return url;
    },

    async postContact() {
      if (this.isForm && this.postUrl) {
        const url = this.addParamsFromFrontUrl(this.dataUrlParams, this.postUrl);
        const result = formManager.processForm(this.models);
        this.loading = true;
        try {
          const response = await apiConnection.post(url, result);
          if (response && !response.haseError) {
            notificationManager.showNotification("success", i18n.global.t("registration.contact.success"));
            this.loading = false;
            const newContact = response?.responses?.[0]?.object;
            if (this.autoSelectValue && newContact) {
              this.processSelectValue(newContact);
              await this.draft(this.currentField);
            }
          }
        } catch {
          this.loading = false;
        }
      }
    },

    setModalContent(response) {
      if (this.isForm) {
        this.models = response;
      } else {
        this.modalData = response;
      }
    },

    processSelectValue(user) {
      if (this.currentField && this.currentField.vars) {
        const newContact = {
          label: user?.fullNameWithCryptedEmail,
          value: String(user?.id),
          data: { id: user?.id },
          attr: [],
          labelTranslationParameters: [],
        };
        this.currentField.vars.value = newContact;
        this.currentField.vars.data = newContact;
        this.currentField.vars?.choices?.push(newContact);
        this.currentField.vars.error = null;
        this.currentField.vars.isInvalid = false;
      }
    },

    checkChildrenToReset(item) {
      Object.values(this.tabs).forEach((tab) => {
        if (tab.blocks && tab.blocks.length) {
          tab.blocks.forEach((block) => {
            const childrenModel = block.models.find((model) => model.vars.name === item);
            if (childrenModel) {
              childrenModel.vars.value = null;
              childrenModel.vars.choice = null;
            }
          });
        }
      });
    },

    async clickButton(button) {
      if (!button.url) return;

      const result = this.processStep(true);
      if (result?.hasError) {
        notificationManager.showNotification("error", i18n.global.t("form_error.verify_fields"));
        return;
      }

      if (this.hasPopup(button.registrationStep?.confirmationPopup)) {
        const confirmed = await this.handleAlert(button.registrationStep?.confirmationPopup, "question");
        if (!confirmed) return;
      }
      this.loading = true;

      let res;
      try {
        res = await registrationManager.clickButton(button.url, result);
      } finally {
        this.loading = false;
      }

      if (res?.hasError) {
        notificationManager.showNotification("error", i18n.global.t("form_error.verify_fields"));
        return;
      }

      if (this.hasPopup(button.registrationStep?.validationPopup)) {
        await this.handleAlert(button.registrationStep?.validationPopup, "success", false);
      } else {
        notificationManager.showNotification("success", i18n.global.t("registration.step.validation.success"));
      }

      const registration = res.responses[0]?.object?.registration;
      this.goToTab(registration?.nextTabId, registration?.cryptedId);
      this.updateCurrentTab(registration?.currentTab);
      this.updateSteps(registration?.steps);
      this.validCurrentRegistration(
        registration?.hasCurrentRegistrationTabBeenValidated,
        registration?.currentRegistrationTabId,
      );
      this.updateNextTab(registration?.nextTab, registration?.nextTabId);
    },

    hasPopup(popup) {
      return popup && Object.keys(popup).length > 0 && popup.enabled;
    },

    async handleAlert(alert, alertType, shouldConfirm = true) {
      const alertConfirmation = await notificationManager.showAlert(
        alertType,
        alert.title ?? "",
        alert.content ?? "",
        true,
        shouldConfirm,
        alert.labelConfirm ?? this.$t("confirm"),
        alert.labelCancel ?? this.$t("cancel"),
        "#000",
        "",
        !!alert.content,
      );
      if (!shouldConfirm) return true;
      return alertConfirmation.isConfirmed;
    },

    async postEditCostForm() {
      const result = formManager.processForm(this.models);
      const response = await registrationManager.postCost(this.registrationCryptedId, this.currentYear, result);
      if (response) {
        notificationManager.showNotification("success", i18n.global.t("registration.quote.edit.cost.success"));
        this.quoteAmountLoading = true;
        await this.getQuoteTableInformation();
        this.quoteAmountLoading = false;
        this.resetModalContent();
      }
    },

    submitModal() {
      switch (this.modalType) {
        case "financial":
          this.postEditCostForm();
          break;
        case "contractDate":
          this.postSpecificCalendar();
          break;
        case "contact":
          this.postContact();
          break;
        case "activeAccount":
          this.activeAccount();
          break;
      }
    },

    async activeAccount() {
      const result = formManager.processForm(this.models);
      this.loading = true;
      const response = await userManager.postUserFirstConnection(result);
      if (response && response.token) {
        setTimeout(() => {
          this.setAppLoading(true);
          const apiPath = loginManager.getApiPath();
          loginManager.setToken(response, apiPath);
          this.fetchUserInfos().then(() => {
            this.setAppLoading(false);
            this.accountActivated = true;
            this.init();
          });
        }, 50);
      }
      this.loading = true;
    },

    async updateCost() {
      if (this.modalType === "financial") {
        this.disabledModalSubmit = true;
        if (this.updateCostDebounceTimer) {
          clearTimeout(this.updateCostDebounceTimer);
        }
        this.updateCostDebounceTimer = setTimeout(async () => {
          if (this.modalType === "financial") {
            const result = formManager.processForm(this.models);
            const response = await registrationManager.updateCost(this.registrationCryptedId, this.currentYear, result);
            this.disabledModalSubmit = false;

            if (!response) return;

            if (response?.response?.fieldsToUpdate && Object.values(response.response.fieldsToUpdate).length) {
              this.updateCostFieldsValue(response.response.fieldsToUpdate);
            }
            this.processAlert(response.response);
          }
        }, 500);
      }
    },

    updateCostFieldsValue(fieldsToUpdate) {
      Object.entries(fieldsToUpdate).forEach((field) => {
        this.models[field[0]].vars.value = field[1];
      });
    },

    processAlert(response) {
      this.alertType = response.hasDiscount ? "success" : "";
      this.alertIcon = response.hasDiscount ? "circle-info" : "";
      this.alertTitle = response.hasDiscount
        ? i18n.global.t("registration.quote.discount", {
            economy: response.diffEuro,
            discount: response.diffPercent,
          })
        : "";
    },

    async getSpecificCalendarForm() {
      if (this.registration && this.registration.trainingCourse?.id && this.registration.registrationGroup?.id) {
        this.quoteContractLoading = true;
        registrationManager
          .getSpecificCalendarForm(
            this.registration.trainingCourse.id,
            this.registration.registrationGroup.id,
            this.current,
            this.$route.params.registration,
          )
          .then((res) => {
            this.models = res;
            this.modalType = "contractDate";
            this.modalTitle = i18n.global.t("registration.quote.edit.date.title");
            this.quoteContractLoading = false;
          });
      }
    },

    async postSpecificCalendar() {
      if (this.registration && this.registration.trainingCourse?.id && this.registration.registrationGroup?.id) {
        const result = formManager.processForm(this.models);
        const calendarFile = this.models?.calendar?.vars?.value;
        this.quoteContractLoading = true;
        registrationManager
          .putRegistrationContractDates(
            this.registration.trainingCourse.id,
            this.registration.registrationGroup.id,
            this.current,
            this.$route.params.registration,
            result,
          )
          .then((res) => {
            this.quoteContractLoading = false;
            if (!res.hasError) {
              notificationManager.showNotification("success", i18n.global.t("registration.quote.edit.date.success"));
              registrationManager.updateRegistrationGroupData(this.registration, result, calendarFile);
            } else if (res.responses[0] && res.responses[0].validationErrors) {
              notificationManager.showNotification("error", i18n.global.t("signed_error"));
            }
          });
      }
    },

    async getQuoteData() {
      if (this.currentTabCode === registrationConstante.REGISTRATION_QUOTE_TAB_CODE && this.registrationCryptedId) {
        await Promise.all([
          this.getQuoteContractInformation(),
          this.getQuoteFinancialInformation(),
          this.getQuoteTableInformation(),
        ]);
      }
    },

    async getSalaryData(stopLoading = true) {
      if (this.currentTabCode === registrationConstante.REGISTRATION_SALARY_TAB_CODE) {
        this.loading = true;
        const response = await registrationManager.form(
          this.$route.params.group,
          this.$route.params.registration,
          "salary",
        );

        this.updateTab(response, registrationConstante.REGISTRATION_SALARY_TAB_CODE);
        if (stopLoading) {
          this.loading = false;
        }
      }
    },

    updateTab(response, code) {
      const tabIndex = Object.values(this.tabs).findIndex((tab) => tab.code === code);

      if (tabIndex !== -1) {
        const key = Object.keys(this.tabs)[tabIndex];
        this.tabs[key] = response;
        this.key++;
      }
    },

    async getQuoteContractInformation() {
      try {
        this.quoteContractLoading = true;
        const res = await registrationManager.getEstimate("contract", {}, this.registrationCryptedId);
        if (!res.hasError && res.response) {
          this.contract = res.response;
        } else {
          this.contract["hasError"] = res.response.validationErrors.message;
        }
      } finally {
        this.quoteContractLoading = false;
      }
    },

    async getQuoteFinancialInformation() {
      try {
        this.quoteFinancialLoading = true;
        const res = await registrationManager.getEstimate("financing", {}, this.registrationCryptedId);
        if (!res.hasError && res.response) {
          this.finacial = res.response;
        } else {
          this.finacial["hasError"] = res.response.validationErrors.message;
        }
      } finally {
        this.quoteFinancialLoading = false;
      }
    },

    async getQuoteTableInformation() {
      try {
        this.quoteAmountLoading = true;
        const res = await registrationManager.getEstimate("tabs", {}, this.registrationCryptedId);
        if (!res.hasError && res.response) {
          this.amount["columns"] = [
            "",
            i18n.global.t("registration.quote.table.columns.trainingCourseCost"),
            i18n.global.t("registration.quote.table.columns.opcoo"),
            i18n.global.t("registration.quote.table.columns.mandatory_participation"),
            i18n.global.t("registration.quote.table.columns.remaining_cost"),
          ];

          this.amount["data"] = res.response.tabs;
        } else {
          this.amount["hasError"] = res.response.validationErrors.message;
        }
      } finally {
        this.quoteAmountLoading = false;
      }
    },

    conditionalChildren(payload, init) {
      if (payload && Object.keys(payload).length) {
        Object.values(this.tabs).forEach((tab) => {
          if (tab.blocks && tab.blocks.length) {
            tab.blocks.forEach((block) => {
              if (block.models && block.models.length) {
                const modelIndex = block.models.findIndex((model) => model.vars.name === payload[0]);
                if (modelIndex !== -1) {
                  const childrenModel = block.models[modelIndex];
                  if (childrenModel && childrenModel.vars) {
                    childrenModel.vars = {
                      ...childrenModel.vars,
                      choices: payload[1].choices,
                      hide: !payload[1].visible,
                      ignoreValidation: !payload[1].visible,
                    };
                    childrenModel.loading = false;

                    block.models.splice(modelIndex, 1, { ...childrenModel });

                    if (!init) {
                      formManager.setAutoSelectedValuesFromChoices(childrenModel, payload[1].value);
                      this.putOneFieldFromConditionalChildren(childrenModel);
                    }
                  }
                }
              }
            });
          }
        });
      }
    },

    putOneFieldFromConditionalChildren(model) {
      const { group, registration } = this.$route.params;
      const registrationField = model?.vars?.attr?.["registration-field"];
      if (model && model.vars) {
        const result = formManager.processField(model, {}, [model]);
        if (registrationField && group && registration) {
          registrationManager.putOneField(group, registrationField, result, registration);
        }
      }
    },

    async fetchConditionalChildren(payload) {
      if (payload.model.vars.attr["registration-field"] && this.registrationCryptedId) {
        const data = {
          value: formManager.getDataFromValue(payload.model.vars.value),
          registrationCryptedId: this.registrationCryptedId,
        };
        this.setLoadingOnConditionnalChildren(payload.model, true);
        const response = await formManager.fetchConditionalChildren(
          data,
          Number(payload.model.vars.attr["registration-field"]),
        );
        if (response) {
          Object.entries(response).forEach((entrie) => {
            this.conditionalChildren(entrie, payload.init);
          });
        }
        this.setLoadingOnConditionnalChildren(payload.model, false);
      }
    },

    setLoadingOnConditionnalChildren(model, loading) {
      model.vars.attr["conditionalChildren"].forEach((item) => {
        Object.values(this.tabs).forEach((tab) => {
          if (tab.blocks && tab.blocks.length) {
            tab.blocks.forEach((block) => {
              if (block && block.models && Object.keys(block.models).length) {
                const childrenModel = block.models.find((model) => model.vars.name === item);
                if (childrenModel && childrenModel.vars) {
                  childrenModel.loading = loading;
                }
              }
            });
          }
        });
      });
    },

    async getEditCostForm(year) {
      this.quoteAmountLoading = true;
      const response = await registrationManager.getEditCostForm(this.registrationCryptedId, year);
      this.modalType = "financial";
      this.currentYear = year;
      this.models = response;
      this.modalTitle = this.$t("registration.quote.edit.cost.title") + " - " + this.$t("year") + " " + year;
      this.quoteAmountLoading = false;
    },

    downloadTrainingSheet() {
      registrationManager.downloadTrainingSheet(this.registrationCryptedId);
    },

    async updateDocumentFile(type) {
      const result = {
        id: null,
        type: "",
      };
      switch (type) {
        case "convention":
          result.id = this.conventionModels?.vars?.value?.id;
          break;
        case "cerfa":
          result.id = this.cerfaModels?.vars?.value?.id;
          break;
      }

      result.type = type;

      if (!result.id) return;

      const response = await registrationManager.postDocument(this.current, this.$route.params.registration, result);
      if (response && response.success) {
        notificationManager.showNotification(
          "success",
          i18n.global.tc("registration.document.update.success", {
            document: i18n.global.t("registration.document." + type + ".title").toLowerCase(),
          }),
        );
      }
    },

    async downloadDocument(type) {
      const response = await registrationManager.downloadDefaultTemplate(type, this.$route.params.registration);
      if (response) {
        messengerMessageManager.processMessage(response);
      }
    },

    ...mapActions(useMainStore, ["setAppLoading", "setBreadcrumbPageTitle"]),
    ...mapActions(useStudeaManagerStore, ["setTrainingName"]),
    ...mapActions(useUserStore, { fetchUserInfos: "fetchUserInfos" }),
    ...mapActions(useRegistrationStore, [
      "pushAlerts",
      "removeAlerts",
      "addAlertToCorrespondingTable",
      "pushAlert",
      "resetAlerts",
    ]),
  },
};
</script>

<style lang="scss" scoped>
.loader-container {
  min-height: 300px;
}
</style>
